import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import {
  PlayArrow,
  Stop,
  Delete,
  Refresh,
  Speed,
  Close,
  ExpandMore,
} from '@mui/icons-material'
import { adminAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminProfilingPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [profilingStatus, setProfilingStatus] = useState(null)
  const [profilingSummary, setProfilingSummary] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [detailsDialog, setDetailsDialog] = useState({ open: false, category: null, entries: [] })
  const [detailsLoading, setDetailsLoading] = useState(false)

  useEffect(() => {
    fetchProfilingStatus()
  }, [])

  const fetchProfilingStatus = async () => {
    try {
      setLoading(true)
      setError('')
      const status = await adminAPI.getProfilingStatus()
      setProfilingStatus(status)

      // If profiling is disabled or has data, fetch summary
      if (status && (status.data_points > 0 || !status.enabled)) {
        fetchProfilingSummary()
      }
    } catch (err) {
      setError(`Failed to load profiling status: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const fetchProfilingSummary = async () => {
    try {
      const summary = await adminAPI.getProfilingSummary()
      setProfilingSummary(summary)
    } catch (err) {
      console.error('Failed to fetch profiling summary:', err)
    }
  }

  const handleEnableProfiling = async () => {
    try {
      setActionLoading(true)
      await adminAPI.enableProfiling()
      await fetchProfilingStatus()
    } catch (err) {
      setError(`Failed to enable profiling: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDisableProfiling = async () => {
    try {
      setActionLoading(true)
      await adminAPI.disableProfiling()
      await fetchProfilingStatus()
    } catch (err) {
      setError(`Failed to disable profiling: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleStartProfiling = async () => {
    try {
      setActionLoading(true)
      await adminAPI.startProfiling()
      setProfilingSummary(null) // Clear old summary
      await fetchProfilingStatus()
    } catch (err) {
      setError(`Failed to start profiling: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleStopProfiling = async () => {
    try {
      setActionLoading(true)
      await adminAPI.stopProfiling()
      await fetchProfilingStatus()
      await fetchProfilingSummary()
    } catch (err) {
      setError(`Failed to stop profiling: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleResetProfiling = async () => {
    try {
      setActionLoading(true)
      await adminAPI.resetProfiling()
      setProfilingSummary(null)
      await fetchProfilingStatus()
    } catch (err) {
      setError(`Failed to reset profiling: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleCategoryClick = async (category) => {
    try {
      setDetailsLoading(true)
      setDetailsDialog({ open: true, category, entries: [] })
      const data = await adminAPI.getProfilingEntries(category)
      setDetailsDialog({ open: true, category, entries: data.entries || [] })
    } catch (err) {
      setError(`Failed to load profiling details: ${err.message}`)
      setDetailsDialog({ open: false, category: null, entries: [] })
    } finally {
      setDetailsLoading(false)
    }
  }

  const handleCloseDetailsDialog = () => {
    setDetailsDialog({ open: false, category: null, entries: [] })
  }

  const formatDuration = (ms) => {
    if (ms < 1) return `${ms.toFixed(2)}ms`
    if (ms < 1000) return `${Math.round(ms)}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleTimeString()
  }

  if (loading) {
    return (
      <AdminPageWrapper title="Performance Profiling">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title="Performance Profiling"
      subtitle="Track and analyze performance of LLM calls, database operations, and tool executions."
    >

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Status Card */}
      {profilingStatus && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Profiler Status
                </Typography>
                <Chip
                  icon={<Speed />}
                  label={profilingStatus.enabled ? 'ENABLED' : 'DISABLED'}
                  color={profilingStatus.enabled ? 'success' : 'default'}
                  size="large"
                  sx={{ fontSize: '1rem', px: 2, py: 2.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Data Points Collected
                </Typography>
                <Typography variant="h4">
                  {profilingStatus.data_points.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={1}>
                {!profilingStatus.enabled ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={actionLoading ? <CircularProgress size={16} /> : <PlayArrow />}
                      onClick={handleStartProfiling}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Start Profiling
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      onClick={handleEnableProfiling}
                      disabled={actionLoading}
                      fullWidth
                      size="small"
                    >
                      Enable Only
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={actionLoading ? <CircularProgress size={16} /> : <Stop />}
                      onClick={handleStopProfiling}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Stop & Show Summary
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Stop />}
                      onClick={handleDisableProfiling}
                      disabled={actionLoading}
                      fullWidth
                      size="small"
                    >
                      Disable Only
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box display="flex" gap={1} mt={3} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<Delete />}
              onClick={handleResetProfiling}
              disabled={actionLoading || profilingStatus.data_points === 0}
              size="small"
            >
              Reset Data
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchProfilingStatus}
              disabled={actionLoading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Paper>
      )}

      {/* Instructions */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          How to Use
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ marginLeft: '1.2rem', marginBottom: 0 }}>
            <li>Click <strong>"Start Profiling"</strong> to reset all data and begin tracking</li>
            <li>Interact with the system (process tickets, run agents, etc.)</li>
            <li>Return here and click <strong>"Stop & Show Summary"</strong> to view results</li>
            <li>Click on any category row to view detailed entries with request/response info</li>
          </ol>
        </Typography>
      </Paper>

      {/* Profiling Summary */}
      {profilingSummary && profilingSummary.stats && Object.keys(profilingSummary.stats).length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Profiling Summary
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="right"><strong>Count</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>Average</strong></TableCell>
                  <TableCell align="right"><strong>Min</strong></TableCell>
                  <TableCell align="right"><strong>Max</strong></TableCell>
                  <TableCell align="right"><strong>%</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(profilingSummary.stats)
                  .sort(([, a], [, b]) => b.total_ms - a.total_ms)
                  .map(([category, stats]) => {
                    const percentage = profilingSummary.total_time > 0
                      ? (stats.total_ms / profilingSummary.total_time * 100)
                      : 0

                    return (
                      <TableRow
                        key={category}
                        hover
                        onClick={() => handleCategoryClick(category)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" sx={{ display: 'flex', alignItems: 'center' }}>
                            {category}
                            <ExpandMore sx={{ ml: 'auto', fontSize: 18 }} />
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{stats.count.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatDuration(stats.total_ms)}</TableCell>
                        <TableCell align="right">{formatDuration(stats.avg_ms)}</TableCell>
                        <TableCell align="right">{formatDuration(stats.min_ms)}</TableCell>
                        <TableCell align="right">{formatDuration(stats.max_ms)}</TableCell>
                        <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                      </TableRow>
                    )
                  })}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>TOTAL</strong></TableCell>
                  <TableCell align="right"><strong>{profilingSummary.total_count.toLocaleString()}</strong></TableCell>
                  <TableCell align="right"><strong>{formatDuration(profilingSummary.total_time)}</strong></TableCell>
                  <TableCell align="right" colSpan={3}>-</TableCell>
                  <TableCell align="right"><strong>100%</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No Data Message */}
      {profilingStatus && profilingStatus.data_points === 0 && !profilingStatus.enabled && (
        <Alert severity="info">
          No profiling data collected yet. Click "Start Profiling" to begin tracking performance metrics.
        </Alert>
      )}

      {profilingStatus && profilingStatus.data_points === 0 && profilingStatus.enabled && (
        <Alert severity="info">
          Profiling is enabled and collecting data. Interact with the system, then click "Stop & Show Summary" to view results.
        </Alert>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {detailsDialog.category} - Detailed Entries
            </Typography>
            <IconButton onClick={handleCloseDetailsDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : detailsDialog.entries.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell align="right"><strong>Duration</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsDialog.entries.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatTimestamp(entry.timestamp)}</TableCell>
                      <TableCell align="right">{formatDuration(entry.duration_ms)}</TableCell>
                      <TableCell>
                        {entry.metadata ? (
                          <Box component="pre" sx={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(entry.metadata, null, 2)}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No metadata</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" p={3}>
              No entries found for this category
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminProfilingPage
